import {AddressElement, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js"
import {useMutation} from "@tanstack/react-query"
import {Radio, Space} from "antd"
import {BaseButton} from "components"
import {FormLabel} from "components/Form"
import {handleError, toastSuccess} from "helpers"
import {useStudentPaymentMethods} from "hooks"
import moment from "moment"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {
    CreateRecurringPaymentPayload,
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency,
    StudentRecurringPayment
} from "types/student-account/student-payment"

type StripeFormProps = {
    profileId: number
    onSuccess: () => void
    amount: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount: number
    cancelAt: moment.Moment | null
    paymentPlanItems?: CreateRecurringPaymentPayload["paymentPlanItems"]
    submitButtonTitle?: string
    preferNewCard?: boolean
}

const PAYMENT_METHOD_NEW = -1

const StripeRecurringPaymentForm = ({
    profileId,
    onSuccess,
    amount,
    currency,
    interval,
    intervalCount,
    cancelAt,
    paymentPlanItems,
    submitButtonTitle,
    preferNewCard = false
}: StripeFormProps) => {
    const {t} = useTranslation(["payment", "common"])
    const stripe = useStripe()
    const elements = useElements()
    const {paymentMethods, refetch: refetchPaymentMethods} = useStudentPaymentMethods({profileId})
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>(
        preferNewCard ? PAYMENT_METHOD_NEW : undefined
    )

    useEffect(() => {
        if (preferNewCard) {
            setSelectedPaymentMethodId(PAYMENT_METHOD_NEW)
            return
        }
        if (!paymentMethods.length) return
        setSelectedPaymentMethodId((prev) => {
            if (prev !== undefined) return prev
            const active = paymentMethods.find((method) => method.isActive) ?? paymentMethods[0]
            return active.paymentMethodId
        })
    }, [paymentMethods, preferNewCard])

    const {isLoading, mutate: addRecurringPayment} = useMutation({
        mutationFn: async (event: React.FormEvent) => {
            event.preventDefault()

            if (!stripe || !elements || !profileId) {
                return
            }

            let paymentMethodId = selectedPaymentMethodId

            if (selectedPaymentMethodId === PAYMENT_METHOD_NEW) {
                const {error, setupIntent} = await stripe.confirmSetup({
                    elements,
                    redirect: "if_required"
                })
                if (error) {
                    throw error
                }
                const paymentMethod = await studentPaymentService.stripeAttachPaymentMethod(
                    profileId,
                    setupIntent.payment_method as string
                )
                paymentMethodId = paymentMethod.paymentMethodId
            }

            if (!paymentMethodId) {
                throw new Error(t("msg.recurringPayment.paymentMethodRequired"))
            }

            return studentPaymentService.createStudentRecurringPayment({
                profileId,
                provider: PaymentProvider.Stripe,
                paymentMethodId,
                amountInCents: Math.round((amount || 0) * 100),
                currency,
                interval,
                intervalCount,
                cancelAt: cancelAt?.toISOString() ?? null,
                paymentPlanItems
            })
        },
        onError: (error) => handleError(error),
        onSuccess: (recurringPayment: StudentRecurringPayment) => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
            refetchPaymentMethods()
            setSelectedPaymentMethodId(recurringPayment.paymentMethodId)
        }
    })

    return (
        <form onSubmit={addRecurringPayment}>
            <div className="mt-24">
                <FormLabel label={t("label.paymentMethods")} />
                <Radio.Group
                    className="mt-24"
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    value={selectedPaymentMethodId}>
                    <Space direction="vertical">
                        {paymentMethods.map((method) => (
                            <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                                {`${method.cardBrand.toUpperCase()} **** **** **** ${method.last4} (${
                                    method.expMonth
                                }/${method.expYear})`}
                            </Radio>
                        ))}
                        <Radio value={PAYMENT_METHOD_NEW}>{t("label.addNewCard")}</Radio>
                    </Space>
                </Radio.Group>

                {selectedPaymentMethodId === PAYMENT_METHOD_NEW && (
                    <>
                        <AddressElement options={{mode: "billing"}} />
                        <PaymentElement className="mt-24" />
                    </>
                )}
            </div>

            <BaseButton
                size="small"
                title={submitButtonTitle ?? t("common:action.add")}
                disabled={!stripe || !elements}
                htmlType="submit"
                loading={isLoading}
                className="w-full mt-24"
            />
        </form>
    )
}
export default StripeRecurringPaymentForm
